import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, Building2, Loader2, CalendarCheck } from "lucide-react";

function DemoForm() {
  const [form, setForm] = useState({ name: "", email: "", kommun: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/demo", form);
      setSent(true);
      toast.success("Tack! Vi hör av oss inom kort.");
    } catch {
      toast.error("Kunde inte skicka – försök igen");
    } finally {
      setLoading(false);
    }
  };
  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-8 text-center" data-testid="demo-form-success">
        <Check className="w-8 h-8 text-emerald-600 mx-auto" />
        <h3 className="mt-3 text-lg font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Tack för din förfrågan!</h3>
        <p className="mt-1 text-sm text-zinc-600">Vi återkommer till {form.email} inom en arbetsdag för att boka en demo.</p>
      </div>
    );
  }
  const cls = "mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm";
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="demo-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Namn *</label>
          <input required value={form.name} onChange={f("name")} data-testid="demo-name" className={cls} />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">E-post *</label>
          <input required type="email" value={form.email} onChange={f("email")} data-testid="demo-email" className={cls} />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Kommun / Organisation</label>
          <input value={form.kommun} onChange={f("kommun")} data-testid="demo-kommun" className={cls} />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Telefon</label>
          <input value={form.phone} onChange={f("phone")} data-testid="demo-phone" className={cls} />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Meddelande</label>
        <textarea rows={3} value={form.message} onChange={f("message")} data-testid="demo-message" className={cls}
          placeholder="Berätta gärna kort om era behov och volymer…" />
      </div>
      <button type="submit" disabled={loading} data-testid="demo-submit"
        className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white px-6 py-3 rounded-sm text-sm font-medium">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />} Boka demo
      </button>
    </form>
  );
}

export default function ForKommuner() {
  return (
    <>
      <Seo
        title="För kommuner – handläggarportal och CRM för bygglov"
        description="Bygglovskompassen för kommuner: CRM-vy för handläggare, AI-stöd som ger kompletta ansökningar, realtidskommunikation med invånare och white-label per kommun."
        path="/for-kommuner"
      />
      <section className="bg-white py-20 lg:py-28" data-testid="for-kommuner-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">För kommuner</div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
              En portal som möter både invånare och handläggare.
            </h1>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              Privatpersoner får AI-stöd som hjälper dem skicka in kompletta ärenden – färre kompletteringar för dig. Handläggare får en CRM-vy med kontroll över alla ärenden, dokument och kommunikation.
            </p>
            <div className="mt-6 space-y-2 text-sm text-zinc-700">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> CRM-API: GET/POST cases, contacts, documents, comments</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> BankID & Google-inlogg</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Realtidskommunikation med invånare</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> White-label per kommun</div>
            </div>
            <div className="mt-8 flex gap-3">
              <Link to="/login" data-testid="muni-cta"
                className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-3 rounded-sm text-sm font-medium">
                Logga in som handläggare <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-700 text-white flex items-center justify-center rounded-sm">
                  <Building2 className="w-5 h-5 stroke-[1.5]" />
                </div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: "Outfit" }}>Stockholms stad</div>
                  <div className="text-xs text-zinc-500">Demohandläggare – Erik Lindberg</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[{ k: "Totalt", v: 124 }, { k: "Aktiva", v: 38 }, { k: "Beslut/v", v: 12 }].map((s) => (
                  <div key={s.k} className="bg-white border border-zinc-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{s.k}</div>
                    <div className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {[
                  { ref: "BYGG-2026-1247", title: "Tillbyggnad villa", status: "Inkommet", color: "bg-blue-50 text-blue-700" },
                  { ref: "BYGG-2026-1244", title: "Nybyggnad enbostadshus", status: "Komplettering", color: "bg-amber-50 text-amber-700" },
                  { ref: "BYGG-2026-1238", title: "Garage 40 kvm", status: "Beslut", color: "bg-emerald-50 text-emerald-700" },
                ].map((c) => (
                  <div key={c.ref} className="bg-white border border-zinc-200 p-3 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{c.ref}</div>
                      <div className="text-sm font-medium">{c.title}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${c.color}`}>{c.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-zinc-50 border-t border-zinc-200 py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Boka demo</div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Se plattformen i praktiken – 30 minuter, anpassat för er kommun.
          </h2>
          <p className="mt-2 text-sm text-zinc-600">Fyll i formuläret så återkommer vi inom en arbetsdag med förslag på tid.</p>
          <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
            <DemoForm />
          </div>
        </div>
      </section>
    </>
  );
}
